import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { environment } from '../../../environments/environment';

/**
 * Loads Google's gtag.js and reports page views — production web only, behind
 * Marketing consent. Configures BOTH the Google Ads tag (`googleAdsId`) and GA4
 * (`googleAnalyticsId`) on the one shared loader, so there's a single script and
 * page_view events reach both destinations.
 *
 * Gated on the ids being present, set ONLY in environment.prod.ts, so nothing
 * loads on local/dev/qa or the native mobile shell. Injected at runtime rather
 * than pasted into index.html (which ships to every environment).
 *
 * Privacy (payroll app): collection fires only on public marketing/auth-entry
 * pages — never the authenticated employer/employee areas or token-bearing
 * links — so no payroll PII reaches Google.
 *
 * Gating collection is subtle: `send_page_view: false` only suppresses the tag's
 * automatic *page_view*. A configured Google Ads tag still auto-beacons
 * remarketing / enhanced-conversion / form-data collection (`/ccm/collect`,
 * `/rmkt/collect`, `/pagead/form-data`) on EVERY SPA navigation, app-wide. So we
 * gate collection with Google Consent Mode: default everything to `denied`, then
 * `granted` only while on a public path (and hard-disable GA off-page). That
 * stops the Ads tag from collecting authenticated route paths + form data.
 */
const PUBLIC_PATHS = new Set<string>([
  '/', '/contact', '/privacy', '/terms', '/auth/login', '/auth/register'
]);

type Gtag = (...args: unknown[]) => void;

/** The consent keys that gate Ads + GA4 collection. */
const CONSENT_KEYS = ['ad_storage', 'ad_user_data', 'ad_personalization', 'analytics_storage'] as const;

@Injectable({ providedIn: 'root' })
export class GoogleAdsService {
  private readonly doc    = inject(DOCUMENT);
  private readonly router = inject(Router);
  private started = false;
  private adsId = '';
  private gaId  = '';

  /** Idempotent. No-op unless an Ads or GA4 id is configured (production only). */
  init(): void {
    this.adsId = environment.googleAdsId ?? '';
    this.gaId  = environment.googleAnalyticsId ?? '';
    if ((!this.adsId && !this.gaId) || this.started) { return; }
    this.started = true;

    this.loadTags(this.adsId, this.gaId);

    this.applyRoute(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.applyRoute(e.urlAfterRedirects));
  }

  /** Grant collection on public pages, deny it everywhere else, and fire the
   *  page_view only when public. Denying via Consent Mode is what actually stops
   *  the Ads tag's remarketing/form-data beacons on authenticated pages. */
  private applyRoute(url: string): void {
    const path = url.split(/[?#]/)[0] || '/';
    const isPublic = PUBLIC_PATHS.has(path);
    const win = this.doc.defaultView as (Window & { gtag?: Gtag; [k: string]: unknown }) | null;
    const gtag = win?.gtag;
    if (typeof gtag !== 'function') { return; }

    const state = isPublic ? 'granted' : 'denied';
    gtag('consent', 'update', Object.fromEntries(CONSENT_KEYS.map(k => [k, state])));

    // Belt-and-suspenders: hard-disable GA4 collection entirely off public pages.
    if (this.gaId) { win![`ga-disable-${this.gaId}`] = !isPublic; }

    if (isPublic) { gtag('event', 'page_view', { page_path: path }); }
  }

  /** Standard gtag.js loader, configuring every supplied id (Ads + GA4) on the
   *  one script, minus the automatic page view (we fire it per route instead). */
  private loadTags(adsId: string, gaId: string): void {
    const win = this.doc.defaultView as (Window & { dataLayer?: unknown[]; gtag?: Gtag }) | null;
    if (!win || win.gtag) { return; }

    const dataLayer = (win.dataLayer = win.dataLayer ?? []);
    // Canonical gtag stub — pushes the `arguments` object verbatim.
    const gtag: Gtag = function (): void {
      // eslint-disable-next-line prefer-rest-params
      dataLayer.push(arguments);
    };
    win.gtag = gtag;

    gtag('js', new Date());
    // Consent Mode: deny everything up front, so the Ads/GA tags collect nothing
    // until applyRoute() grants it on a public page. Must precede `config`.
    gtag('consent', 'default', {
      ...Object.fromEntries(CONSENT_KEYS.map(k => [k, 'denied'])),
      wait_for_update: 500
    });
    if (adsId) { gtag('config', adsId, { send_page_view: false }); }
    if (gaId)  { gtag('config', gaId,  { send_page_view: false }); }

    const script = this.doc.createElement('script');
    script.async = true;
    // crossorigin so that if gtag.js throws, window.onerror gets the real message
    // instead of a masked cross-origin "Script error." (Google serves it with CORS).
    script.crossOrigin = 'anonymous';
    // The loader id is just which library bundle to fetch — both ids are still
    // configured above and both receive the page_view events.
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(adsId || gaId)}`;
    this.doc.head.appendChild(script);
  }
}
